import { c as _c } from "react/compiler-runtime";
import { RechartsBar as Bar, RechartsRectangle as Rectangle, useRechartsActiveTooltipDataPoints as useActiveTooltipDataPoints, type RechartsBarProps as BarProps, type RechartsBarShapeProps as BarShapeProps } from '@precis-digital/kurama/recharts';
import type { ReactElement } from 'react';
import { useChartElementContext } from '../../hooks/chartElementContext';
import type { SecondAxisTypes } from '../../internalTypes';
import { getSecondAxisDomain } from '../../utils/chart';
import { isBelowDomain } from '../../utils/generics';
import { useChartCardDefaults } from '../../hooks/useChartCardDefaults';
type DynamicBarProps = BarProps & {
  axisType: SecondAxisTypes;
};
const renderShape = (axisDomain: readonly [number, number] | undefined, shapeProps: BarShapeProps): ReactElement | null => {
  if (axisDomain == null) {
    return <Rectangle {...shapeProps} />;
  }
  const {
    value
  } = shapeProps;
  if (typeof value === 'number') {
    if (isBelowDomain(value, axisDomain)) {
      return null;
    }
  } else if (isBelowDomain(value[0], axisDomain) && isBelowDomain(value[1], axisDomain)) {
    return null;
  }
  return <Rectangle {...shapeProps} />;
};
const DynamicBar = t0 => {
  const $ = _c(15);
  let axisType;
  let children;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      axisType,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = axisType;
    $[2] = children;
    $[3] = props;
  } else {
    axisType = $[1];
    children = $[2];
    props = $[3];
  }
  const defaults = useChartCardDefaults();
  const activeDataRow = useActiveTooltipDataPoints();
  const {
    chartAxisIdsAndDomains
  } = useChartElementContext();
  let t1;
  if ($[4] !== axisType || $[5] !== chartAxisIdsAndDomains) {
    t1 = getSecondAxisDomain(axisType, chartAxisIdsAndDomains);
    $[4] = axisType;
    $[5] = chartAxisIdsAndDomains;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const axisDomain = t1;
  let t2;
  if ($[7] !== activeDataRow || $[8] !== axisDomain || $[9] !== defaults) {
    t2 = shapeProps => {
      if (shapeProps.isActive || activeDataRow == null) {
        return renderShape(axisDomain, shapeProps);
      } else {
        return renderShape(axisDomain, {
          ...shapeProps,
          fillOpacity: defaults.others.nonActiveFillOpacity
        });
      }
    };
    $[7] = activeDataRow;
    $[8] = axisDomain;
    $[9] = defaults;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const shape = t2;
  let t3;
  if ($[11] !== children || $[12] !== props || $[13] !== shape) {
    t3 = <Bar {...props} shape={shape} activeBar={shape}>{children}</Bar>;
    $[11] = children;
    $[12] = props;
    $[13] = shape;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  return t3;
};
export default DynamicBar;